/**
 * Reusable Modal Component
 *
 * A flexible modal component with body scroll lock, focus trap, and proper accessibility.
 * Based on EnquiryModal implementation.
 *
 * @package
 */

import { useEffect, useRef, createPortal } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { useFocusTrap } from '../helpers/useFocusTrap';
import Icon from './Icon';

/**
 * Modal Component
 *
 * @param {Object}   props                  - Component props.
 * @param {boolean}  props.isOpen           - Whether the modal is open.
 * @param {Function} props.onClose          - Callback when modal is closed.
 * @param {string}   props.title            - Modal title (optional if titleId is provided).
 * @param {string}   props.titleId          - Custom ID for title element (for ARIA).
 * @param {string}   props.descriptionId    - Custom ID for description element (for ARIA).
 * @param {boolean}  props.showCloseButton  - Whether to show the close button (default: true).
 * @param {string}   props.className        - Additional CSS class for modal content.
 * @param {string}   props.overlayClassName - Additional CSS class for modal overlay.
 * @param {boolean}  props.closeOnBackdrop  - Whether clicking backdrop closes modal (default: true).
 * @param {boolean}  props.closeOnEscape    - Whether pressing Escape closes modal (default: true).
 * @param {boolean}  props.wrapInBody       - Whether to wrap children in .configurator-modal-body (default: true).
 * @param {*}        props.children         - Modal content.
 */
const Modal = ( {
	isOpen,
	onClose,
	title,
	titleId = 'modal-title',
	descriptionId,
	showCloseButton = true,
	className = '',
	overlayClassName = '',
	closeOnBackdrop = true,
	closeOnEscape = true,
	wrapInBody = true,
	children,
} ) => {
	const modalRef = useRef( null );

	useFocusTrap( isOpen, modalRef );

	useEffect( () => {
		if ( ! isOpen ) {
			return;
		}
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const handleEsc = e => {
			if ( closeOnEscape && e.key === 'Escape' ) {
				onClose();
			}
		};

		document.addEventListener( 'keydown', handleEsc );

		return () => {
			document.body.style.overflow = originalOverflow;
			document.removeEventListener( 'keydown', handleEsc );
		};
	}, [ isOpen, onClose, closeOnEscape ] );

	const handleBackdropClick = e => {
		if ( closeOnBackdrop && e.target === e.currentTarget ) {
			onClose();
		}
	};

	if ( ! isOpen ) {
		return null;
	}

	const modalAriaProps = {
		role: 'dialog',
		'aria-modal': 'true',
		'aria-labelledby': titleId,
	};

	if ( descriptionId ) {
		modalAriaProps[ 'aria-describedby' ] = descriptionId;
	}

	return createPortal(
		<div
			className={ `configurator-modal-overlay ${ overlayClassName }` }
			onClick={ handleBackdropClick }
			onKeyDown={ e => {
				// Only when the overlay itself is the target: key presses inside the
				// modal's form fields bubble up here and must not be suppressed.
				if ( ( e.key === 'Enter' || e.key === ' ' ) && e.target === e.currentTarget ) {
					e.preventDefault();
					handleBackdropClick( e );
				}
			} }
			role="button"
			tabIndex={ -1 }
			aria-label={ __( 'Close modal', 'at-vehicle-configurator' ) }
		>
			<div
				ref={ modalRef }
				className={ `configurator-modal-content ${ className }` }
				{ ...modalAriaProps }
			>
				{ showCloseButton && (
					<button
						type="button"
						className="configurator-modal-close"
						onClick={ onClose }
						aria-label={ __( 'Close modal', 'at-vehicle-configurator' ) }
					>
						<span className="screen-reader-text">{ __( 'Close', 'at-vehicle-configurator' ) }</span>
						<Icon name="close" size="medium" />
					</button>
				) }
				{ wrapInBody ? (
					<div className="configurator-modal-body">
						{ title && <h2 id={ titleId }>{ title }</h2> }
						{ children }
					</div>
				) : (
					<>
						{ title && <h2 id={ titleId }>{ title }</h2> }
						{ children }
					</>
				) }
			</div>
		</div>,
		document.body
	);
};

export default Modal;
